import { PODCAST_CONFIG } from "./podcast-config";
import type { PodcastEpisode } from "./types";

export const PODCAST_RSS_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

export type PodcastFeedState =
  | { status: "loading"; episodes: PodcastEpisode[] }
  | { status: "ready"; episodes: PodcastEpisode[] }
  | { status: "error"; episodes: PodcastEpisode[] };

function cleanDescription(value: string) {
  const spacedValue = value.replace(/<\/(p|div|li|h[1-6])>/gi, "$& ");
  const document = new DOMParser().parseFromString(spacedValue, "text/html");
  return document.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
}

function cleanDuration(value?: string) {
  if (!value) return undefined;
  const parts = value.split(":");
  if (parts.length === 3 && Number(parts[0]) === 0) {
    return `${Number(parts[1])}:${parts[2].padStart(2, "0")}`;
  }
  return value;
}

function readNumber(item: Element, tagName: string) {
  const value = Number(item.getElementsByTagName(tagName)[0]?.textContent);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function normalizeEpisodeType(value?: string) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export function isPodcastTrailer(episode: PodcastEpisode) {
  const episodeType = normalizeEpisodeType(episode.episodeType || episode.type);
  return episodeType === "trailer";
}

export function isCountedPodcastEpisode(episode: PodcastEpisode) {
  return !isPodcastTrailer(episode);
}

export function getLatestPodcastEpisode(episodes: PodcastEpisode[]) {
  return episodes.find(isCountedPodcastEpisode);
}

export function podcastEpisodeSlug(episode: Pick<PodcastEpisode, "slug" | "title">) {
  if (episode.slug) return episode.slug;
  return (
    episode.title
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 100) || "episodio"
  );
}

export function parsePodcastEpisodes(xml: string) {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  if (document.querySelector("parsererror")) throw new Error("El RSS no es válido.");

  return Array.from(document.querySelectorAll("item"))
    .map((item): PodcastEpisode | null => {
      const title = item.querySelector("title")?.textContent?.trim() ?? "";
      const publishedAt = item.querySelector("pubDate")?.textContent?.trim() ?? "";
      const publishedDate = new Date(publishedAt);

      if (!title) return null;

      return {
        id:
          item.querySelector("guid")?.textContent?.trim() ?? `${title}-${publishedDate.getTime()}`,
        title,
        description: cleanDescription(item.querySelector("description")?.textContent ?? ""),
        publishedAt: Number.isNaN(publishedDate.getTime())
          ? new Date().toISOString()
          : publishedDate.toISOString(),
        duration: cleanDuration(
          item.getElementsByTagName("itunes:duration")[0]?.textContent?.trim(),
        ),
        url: item.querySelector("link")?.textContent?.trim() ?? PODCAST_CONFIG.rssUrl ?? "",
        season: readNumber(item, "itunes:season") ?? 1,
        episode: readNumber(item, "itunes:episode"),
        episodeType:
          item.getElementsByTagName("itunes:episodeType")[0]?.textContent?.trim() ?? "full",
      };
    })
    .filter((episode): episode is PodcastEpisode => episode !== null)
    .sort(
      (first, second) =>
        new Date(second.publishedAt).getTime() - new Date(first.publishedAt).getTime(),
    );
}
